// src/api/Inquiry.js
import { http, resolveAssetUrl } from "../lib/http";
import { normalizeWordPressUserID } from "../Utils/UserIdentity";

/* ------------------------------ helpers ------------------------------ */

function normalizeLang(lang) {
  const s = String(lang || "")
    .toUpperCase()
    .trim();
  if (s === "EN" || s === "JP" || s === "ZH") return s;
  return "ZH";
}

function withLangParams(params = {}, lang) {
  return { ...(params || {}), lang: normalizeLang(lang) };
}

/**
 * IMPORTANT:
 * inquiryID contains dates like "2026/01/06" which becomes "%2F".
 * Many servers (Apache) may decode %2F before routing -> route breaks.
 *
 * Workaround: double-encode "%2F" => "%252F" for path parameters.
 * Then backend can rawurldecode() to recover.
 */
function encodePathParam(val) {
  const s = encodeURIComponent(String(val ?? ""));
  return s.replace(/%2F/gi, "%252F");
}

/**
 * ✅ IMPORTANT:
 * Some servers/plugins may decode %2F in ANY path segment (even if not intended).
 * For safety, we also double-encode slashes for userID when userID is a WPGraphQL global ID:
 * "dXNlcjoz" (no slash) is fine, but if you ever pass something with "/", it won’t break routes.
 */
function encodePathSeg(val) {
  // keep same behavior as encodePathParam (safe for %2F)
  return encodePathParam(val);
}

const normalizeEmail = (v) =>
  String(v ?? "")
    .trim()
    .toLowerCase();

const isValidEmail = (v) => {
  const email = normalizeEmail(v);
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
};

/**
 * Backend is returning keys/values like:
 *   "u96fbu5b50u90f5u4ef6"  (should be "電子郵件")
 *   "u65b0u5317u5e02"      (should be "新北市")
 *
 * This decodes sequences of "u" + 4-hex into real unicode.
 */
function decodeU4Sequence(str) {
  const s = String(str ?? "");
  // must be entirely uXXXXuXXXX...
  if (!/^u[0-9a-fA-F]{4}(u[0-9a-fA-F]{4})*$/.test(s)) return s;

  const parts = s.split("u").filter(Boolean);
  let out = "";
  for (const hex of parts) {
    const code = parseInt(hex, 16);
    if (!Number.isFinite(code)) return s;
    out += String.fromCharCode(code);
  }
  return out;
}

function deepDecodeU4(obj) {
  if (!obj) return obj;

  if (Array.isArray(obj)) return obj.map(deepDecodeU4);

  if (typeof obj === "string") return decodeU4Sequence(obj);

  if (typeof obj !== "object") return obj;

  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    const dk = decodeU4Sequence(k);
    out[dk] = deepDecodeU4(v);
  }
  return out;
}

function getProductNameFromAny(it) {
  const p = it?.product;

  const mainZH = p?.main;
  const mainEN = p?.maintranslationEN;
  const mainJP = p?.maintranslationJP;

  return (
    it?.productName ||
    p?.productName ||
    p?.name ||
    mainZH?.["產品名稱"] ||
    mainZH?.name ||
    mainEN?.["產品名稱"] ||
    mainEN?.name ||
    mainJP?.["產品名稱"] ||
    mainJP?.name ||
    mainZH?.variantName ||
    ""
  );
}

function getParentIDFromAny(it) {
  const p = it?.product;
  return it?.parentID || p?.parentID || null;
}

/**
 * ✅ Extract ALL images from any possible backend shape.
 * Returns: string[] (resolved URLs) so UI never sees localhost.
 */
function getAllImagesFromAny(it) {
  const p = it?.product;

  const candidates = [it?.images, p?.images, p?.main?.images];
  const out = [];

  for (const arr of candidates) {
    if (!Array.isArray(arr)) continue;
    for (const x of arr) {
      if (!x) continue;

      if (typeof x === "string") {
        out.push(x);
        continue;
      }

      if (typeof x === "object") {
        const v = x.image || x.url || x.path || x.src;
        if (v) out.push(v);
      }
    }
  }

  const fallback =
    it?.image || it?.imageUrl || p?.image || p?.imageUrl || p?.mainImage || "";

  if (fallback) out.push(fallback);

  const seen = new Set();
  return out
    .map((s) => String(s || "").trim())
    .filter((s) => {
      if (!s || seen.has(s)) return false;
      seen.add(s);
      return true;
    })
    .map(resolveAssetUrl)
    .filter(Boolean);
}

/* ------------------------------ normalizers ------------------------------ */

function normalizeInquiry(wrapper) {
  if (!wrapper || !wrapper.inquiry) return null;
  const inq = wrapper.inquiry;

  const decodedInfo = deepDecodeU4(inq.info || null);

  const items = (inq.items || []).map((it) => {
    const images = getAllImagesFromAny(it);

    return {
      productID: it.productID,
      qty: it.qty,
      productName: getProductNameFromAny(it),
      parentID: getParentIDFromAny(it) || "",
      images,
      image: images[0] || "",
    };
  });

  return {
    inquiryID: inq.inquiryID,
    userID: inq.userID,
    status: inq.status,
    inquiredDate: inq.inquiredDate || null,
    finishedDate: inq.finishedDate || null,
    info: decodedInfo,
    items,
  };
}

function normalizeInquiryRow(row) {
  if (!row) return null;

  const decodedInfo = deepDecodeU4(row.info || null);

  const items = (row.items || []).map((it) => {
    const images = getAllImagesFromAny(it);

    return {
      id: it.id,
      inquiryID: it.inquiryID,
      productID: it.productID,
      qty: it.qty,
      productName: getProductNameFromAny(it),
      parentID: getParentIDFromAny(it) || "",
      images,
      image: images[0] || "",
    };
  });

  return {
    inquiryID: row.inquiryID,
    userID: row.userID,
    status: row.status,
    inquiredDate: row.inquiredDate || null,
    finishedDate: row.finishedDate || null,
    info: decodedInfo,
    items,
  };
}

function normalizeInquiryList(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map(normalizeInquiryRow).filter(Boolean);
}

/* -------------------------------- API -------------------------------- */

export const InquiryAPI = {
  /**
   * ✅ WP REST: /wp-json/api/inquiry/pending/count/{userID}?lang=...
   * NOTE: userID may be WPGraphQL global id (base64).
   */
  async getPendingCount(
    userID,
    { lang, skipLoading = false } = {},
  ) {
    const uid = normalizeWordPressUserID(userID);
    if (!uid) return { count: 0 };

    const res = await http.get(
      `/inquiry/pending/count/${encodePathSeg(uid)}`,
      {
        params: withLangParams({}, lang),
        skipLoading,
      },
    );

    const data = res?.data ?? res ?? {};
    const count = Number(data?.count ?? data?.data?.count ?? 0) || 0;
    return { ...data, count };
  },

  /**
   * ✅ NEW
   * WP REST: /wp-json/api/inquiry/history-non-pending/count/{userID}?lang=...
   */
  async getHistoryNonPendingCount(userID, { lang } = {}) {
    const uid = normalizeWordPressUserID(userID);
    if (!uid) return { count: 0 };

    const res = await http.get(
      `/inquiry/history-non-pending/count/${encodePathSeg(uid)}`,
      {
        params: withLangParams({}, lang),
      },
    );

    const data = res?.data ?? res ?? {};
    const count = Number(data?.count ?? data?.data?.count ?? 0) || 0;
    return { ...data, count };
  },

  async getCountsByStatus(userID, { lang } = {}) {
    const uid = normalizeWordPressUserID(userID);
    if (!uid) return { pending: 0, processing: 0, finished: 0, checkedout: 0 };

    const res = await http.get(`/inquiry/counts/${encodePathSeg(uid)}`, {
      params: withLangParams({}, lang),
    });
    return res?.data ?? res;
  },

  async createInquiry({ userID, items, lang } = {}) {
    const uid = normalizeWordPressUserID(userID);
    const res = await http.post(`/inquiry`, {
      userID: uid,
      items: Array.isArray(items) ? items : [],
      lang: normalizeLang(lang),
    });
    return res?.data ?? res;
  },

  async addInfo(payload = {}, { lang } = {}) {
    const next = { ...(payload || {}) };
    const info = { ...(next.info || {}) };

    if (typeof info["電子郵件"] !== "undefined") {
      info["電子郵件"] = normalizeEmail(info["電子郵件"]);
    }

    if (
      typeof next["電子郵件"] !== "undefined" &&
      typeof info["電子郵件"] === "undefined"
    ) {
      info["電子郵件"] = normalizeEmail(next["電子郵件"]);
    }

    const res = await http.post(`/inquiry/info`, {
      userID: normalizeWordPressUserID(next.userID),
      inquiryID: String(next.inquiryID || "").trim(),
      info,
      lang: normalizeLang(lang),
    });

    return res?.data ?? res;
  },

  async getCurrentInquiryFromUser(userID, { lang } = {}) {
    const uid = normalizeWordPressUserID(userID);
    if (!uid) return { raw: null, inquiry: null };

    const res = await http.get(`/inquiry/pending/${encodePathSeg(uid)}`, {
      params: withLangParams({}, lang),
    });

    return {
      raw: res?.data ?? res,
      inquiry: normalizeInquiry(res?.data ?? res),
    };
  },

  async history(userID, { lang } = {}) {
    const uid = normalizeWordPressUserID(userID);
    if (!uid) return [];

    const res = await http.get(`/inquiry/history/${encodePathSeg(uid)}`, {
      params: withLangParams({}, lang),
    });
    return normalizeInquiryList(res?.data ?? res);
  },

  async historyNonPending(userID, { lang } = {}) {
    const uid = normalizeWordPressUserID(userID);
    if (!uid) return [];

    const res = await http.get(
      `/inquiry/history-non-pending/${encodePathSeg(uid)}`,
      {
        params: withLangParams({}, lang),
      },
    );
    return normalizeInquiryList(res?.data ?? res);
  },

  async updateInquiry({ userID, inquiryID, items, lang } = {}) {
    const res = await http.patch(`/inquiry`, {
      userID: normalizeWordPressUserID(userID),
      inquiryID: String(inquiryID || "").trim(),
      items: Array.isArray(items) ? items : [],
      lang: normalizeLang(lang),
    });
    return res?.data ?? res;
  },

  async deleteItem(inquiryID, productID, { lang } = {}) {
    const iid = String(inquiryID || "").trim();
    const pid = String(productID || "").trim();

    const res = await http.delete(
      `/inquiry/${encodePathParam(iid)}/item/${encodePathSeg(pid)}`,
      { params: withLangParams({}, lang) },
    );
    return res?.data ?? res;
  },

  async deleteInquiry(inquiryID, { lang } = {}) {
    const iid = String(inquiryID || "").trim();
    const res = await http.delete(`/inquiry/${encodePathParam(iid)}`, {
      params: withLangParams({}, lang),
    });
    return res?.data ?? res;
  },

  /**
   * ✅ IMPORTANT FIX:
   * checkout must send a JSON body that includes 電子郵件
   *
   * Usage:
   *   InquiryAPI.checkout(inquiryID, { 電子郵件: "a@b.com" }, { lang })
   */
  async checkout(inquiryID, payload = {}, { lang } = {}) {
    const iid = String(inquiryID || "").trim();
    const body = { ...(payload || {}), lang: normalizeLang(lang) };

    if (typeof body["電子郵件"] !== "undefined") {
      body["電子郵件"] = normalizeEmail(body["電子郵件"]);
    }

    if (
      typeof body["電子郵件"] !== "undefined" &&
      body["電子郵件"] !== "" &&
      !isValidEmail(body["電子郵件"])
    ) {
      const err = new Error("Customer email is required and must be valid.");
      err.response = {
        data: {
          error: "Customer email is required and must be valid.",
          field: "電子郵件",
        },
      };
      throw err;
    }

    const res = await http.post(
      `/inquiry/${encodePathParam(iid)}/checkout`,
      body,
      { params: withLangParams({}, lang) },
    );
    return res?.data ?? res;
  },

  async process(inquiryID, { lang } = {}) {
    const iid = String(inquiryID || "").trim();
    const res = await http.post(
      `/inquiry/${encodePathParam(iid)}/process`,
      {},
      { params: withLangParams({}, lang) },
    );
    return res?.data ?? res;
  },

  async finish(inquiryID, { lang } = {}) {
    const iid = String(inquiryID || "").trim();
    const res = await http.post(
      `/inquiry/${encodePathParam(iid)}/finish`,
      {},
      { params: withLangParams({}, lang) },
    );
    return res?.data ?? res;
  },

  async getCheckedoutInquiryFromUser(userID, { lang } = {}) {
    const uid = normalizeWordPressUserID(userID);
    if (!uid) return null;

    const res = await http.get(`/inquiry/checkedout/${encodePathSeg(uid)}`, {
      params: withLangParams({}, lang),
    });
    return normalizeInquiryRow(res?.data ?? res);
  },

  async getProcessInquiryFromUser(userID, { lang } = {}) {
    const uid = normalizeWordPressUserID(userID);
    if (!uid) return null;

    const res = await http.get(`/inquiry/processing/${encodePathSeg(uid)}`, {
      params: withLangParams({}, lang),
    });
    return normalizeInquiryRow(res?.data ?? res);
  },

  async getFinishedInquiryFromUser(userID, { lang } = {}) {
    const uid = normalizeWordPressUserID(userID);
    if (!uid) return null;

    const res = await http.get(`/inquiry/finished/${encodePathSeg(uid)}`, {
      params: withLangParams({}, lang),
    });
    return normalizeInquiryRow(res?.data ?? res);
  },

  async cancel(inquiryID, userID, { lang } = {}) {
    const safeInquiryID = String(inquiryID || "").trim();
    const safeUserID = normalizeWordPressUserID(userID);

    if (!safeInquiryID || !safeUserID) {
      throw new Error("inquiryID and userID are required");
    }

    const res = await http.post("/inquiry/cancel", {
      inquiryID: safeInquiryID,
      userID: safeUserID,
      lang: normalizeLang(lang),
    });

    return res?.data ?? res;
  },
};

export default InquiryAPI;
